import { useState, type FormEvent } from "react"
import { useNavigate, Navigate, Link } from "react-router-dom"
import { motion } from "framer-motion"
import { LogIn } from "lucide-react"
import { useAuth } from "../../hooks/useAuth"
import logo from "../../assets/logo.png"

export default function LoginPage() {
  const { login, user } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  if (user) {
    const dest = user.role === "member" ? "/member" : "/dashboard"
    return <Navigate to={dest} replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      const result = await login(email, password)
      navigate(result === "member" ? "/member" : "/dashboard", { replace: true })
    } catch {
      setError("Email o contraseña incorrectos.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background bg-grid-pattern">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="gradient-card rounded-2xl border border-white/10 p-8 shadow-2xl">
          {/* Logo + Title */}
          <div className="mb-8 flex flex-col items-center gap-4">
            <img src={logo} alt="Science Fitness" className="h-16 w-auto" />
            <div className="text-center">
              <h1
                className="text-2xl font-bold tracking-widest text-primary glow-cyan-text uppercase"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Science Fitness
              </h1>
              <p className="mt-1 text-sm text-muted">Portal del Entrenador</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted uppercase tracking-wide">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="trainer@sciencefitness.com"
                required
                className="w-full rounded-lg border border-white/10 bg-surface-elevated px-4 py-3 text-white placeholder-muted/50 focus:border-primary"
              />
            </div>

            <div>
              <div className="mb-1.5">
                <label className="text-xs font-medium text-muted uppercase tracking-wide">Contraseña</label>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-lg border border-white/10 bg-surface-elevated px-4 py-3 text-white placeholder-muted/50 focus:border-primary"
              />
            </div>

            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-lg bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger"
              >
                {error}
              </motion.p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary py-3 font-semibold text-black transition hover:opacity-90 glow-cyan disabled:opacity-50"
            >
              <LogIn size={16} />
              {loading ? "Ingresando..." : "Iniciar Sesión"}
            </button>
          </form>

          <div className="mt-6 flex flex-col items-center gap-2 text-sm text-muted">
            <Link to="/forgot-password" className="text-primary hover:text-primary/80 transition">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
